import React, {useContext, useEffect, useState} from "react";
import {Box, Button, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowTurnForwardIcon, CallEnd01Icon, Mic01Icon, MicOff01Icon, ViewIcon} from "@hugeicons/core-free-icons";
import {useNavigate} from "react-router-dom";
import {useWebrtc} from "../../context/WebrtcContext";
import {SignInContext} from "../../context/SignInContext";
import {ConversationRepository} from "../../repositories/ConversationRepository";
import {RedirectCallDialog} from "./RedirectCallDialog";
import {LiveAvatar} from "../../pages/registry-project/dashboard/components/conversationUi";
import {formatTime} from "../../utils/functions";

export const roundSx = (bg, color = "#fff", size = 40) => ({
  width: size, height: size, borderRadius: "50%", bgcolor: bg, color, "&:hover": {bgcolor: bg, filter: "brightness(0.92)"},
});

export const isCallLive = (call) => Boolean(call)
  && call.state === "active" && (!call.outbound || call.phase === "customer_answered");

export const CallTimer = ({startedAt}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return <>{formatTime(Math.max(0, (now - startedAt) / 1000))}</>;
};

export const useOpenCallConversation = (call) => {
  const navigate = useNavigate();
  const {currentUser} = useContext(SignInContext);
  return async () => {
    if (!call?.projectId || !call?.conversationId) return;
    let conversationId = call.conversationId;
    try {
      const {data} = await ConversationRepository.getConversations({
        projectId: call.projectId, type: "AGENT_CALL", agent: currentUser?.username, page: 0, size: 25,
      });
      const next = (data?.content || []).find(item => String(item.previousConversationId) === String(call.conversationId));
      if (next?.id) conversationId = next.id;
    } catch (e) {
      console.error("Failed to find the conversation that continues the call's", e);
    }
    const base = call.subProjectId
      ? `/registry-project/${call.projectId}/${call.subProjectId}`
      : `/registry-project/${call.projectId}`;
    navigate(`${base}/supervision?conversation=${encodeURIComponent(conversationId)}`);
  };
};

export const ActiveCallCard = ({onOpened}) => {
  const {call, hangup, toggleMute} = useWebrtc();
  const openConversation = useOpenCallConversation(call);
  const [redirectOpen, setRedirectOpen] = useState(false);
  useEffect(() => {
    if (!call) setRedirectOpen(false);
  }, [call]);
  if (!isCallLive(call)) return null;
  const canRedirect = call.conversationId && !call.outbound;

  return (
    <Box sx={{m: 1.5, p: 1.75, borderRadius: "16px", bgcolor: "rgba(132, 145, 247, 0.08)", flexShrink: 0}}>
      <Stack direction={"row"} alignItems={"center"} gap={1.5}>
        <LiveAvatar size={40} iconSize={18}/>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8491F7", textTransform: "uppercase"}}>
            On call · <CallTimer startedAt={call.startedAt}/>
          </Typography>
          <Typography noWrap sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>{call.caller}</Typography>
          <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>
            {call.group ? `Group ${call.group.name || call.group.number || call.group.id} · ` : ""}
            {call.conversationId ? `Conversation #${call.conversationId}` : "Browser call"}
          </Typography>
        </Box>
      </Stack>

      {call.conversationId && call.projectId && (
        <Button
          onClick={() => openConversation().then(() => onOpened?.())} fullWidth
          startIcon={<HugeiconsIcon icon={ViewIcon} size={16}/>}
          sx={{
            mt: 1.5, height: 36, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
            color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
          }}
        >
          Open conversation
        </Button>
      )}

      <Stack direction={"row"} justifyContent={"center"} gap={2} sx={{mt: 1.5}}>
        <Tooltip title={call.muted ? "Unmute" : "Mute"}>
          <IconButton
            onClick={toggleMute} aria-label={call.muted ? "Unmute" : "Mute"}
            sx={call.muted ? roundSx("#23264D", "#fff", 44) : roundSx("#fff", "#23264D", 44)}
          >
            <HugeiconsIcon icon={call.muted ? MicOff01Icon : Mic01Icon} size={20}/>
          </IconButton>
        </Tooltip>
        {canRedirect && (
          <Tooltip title={"Redirect call"}>
            <IconButton onClick={() => setRedirectOpen(true)} aria-label={"Redirect call"} sx={roundSx("#fff", "#23264D", 44)}>
              <HugeiconsIcon icon={ArrowTurnForwardIcon} size={20}/>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={"Hang up"}>
          <IconButton onClick={hangup} aria-label={"Hang up"} sx={roundSx("#E5484D", "#fff", 44)}>
            <HugeiconsIcon icon={CallEnd01Icon} size={20}/>
          </IconButton>
        </Tooltip>
      </Stack>
      <RedirectCallDialog open={redirectOpen} onClose={() => setRedirectOpen(false)} call={call}/>
    </Box>
  );
};
